import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const GOAL_PERIODS = ['day_1_30', 'day_31_60', 'day_61_90'];
export const GOAL_STATUSES = ['set', 'in_progress', 'achieved', 'partially_achieved', 'missed', 'cancelled'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useObGoalsApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/onboarding-goals?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const start = useCallback(async (id) => {
    try { await api.post(`/v1/onboarding-goals/${id}/start`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const cancel = useCallback(async (id) => {
    try { await api.post(`/v1/onboarding-goals/${id}/cancel`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const agreeManager = useCallback(async (id) => {
    try { await api.post(`/v1/onboarding-goals/${id}/agree-manager`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const score = useCallback(async (id, body) => {
    try { await api.post(`/v1/onboarding-goals/${id}/score`, body); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  // Form value ↔ API value (ObGoalRequest). Empty optional fields are sent as null.
  const toPayload = useCallback((formData) => ({
    onboardingId: formData.onboardingId || null,
    period: formData.period || 'day_1_30',
    title: (formData.title || '').trim(),
    description: formData.description || null,
    measurementCriteria: formData.measurementCriteria || null,
  }), []);

  const toFormValues = useCallback((row) => ({
    id: row.id,
    onboardingId: row.onboardingId || null,
    period: row.period || 'day_1_30',
    title: row.title || '',
    description: row.description || '',
    measurementCriteria: row.measurementCriteria || '',
  }), []);

  const create = useCallback(async (formData) => {
    try { await api.post('/v1/onboarding-goals', toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const update = useCallback(async (id, formData) => {
    try { await api.put(`/v1/onboarding-goals/${id}`, toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const columns = useMemo(() => {
    const periodLabel = (s) => t(`onboardingGoal.period.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`onboardingGoal.status.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('onboardingGoal.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'title',
        label: t('onboardingGoal.title'),
        options: {
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
        },
      },
      {
        name: 'period',
        label: t('onboardingGoal.periodLabel'),
        options: {
          ...multiSelectFilter(t('onboardingGoal.periodLabel'), GOAL_PERIODS, periodLabel),
          customBodyRender: (v) => periodLabel(v),
        },
      },
      {
        name: 'status',
        label: t('onboardingGoal.statusLabel'),
        options: {
          ...multiSelectFilter(t('onboardingGoal.statusLabel'), GOAL_STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      numericFilterColumn('managerScore', t('onboardingGoal.managerScore'), { decimals: 0 }),
      {
        name: 'agreedByEmployeeAt',
        label: t('onboardingGoal.agreedByEmployee'),
        options: { filter: false, sort: false, customBodyRender: (v) => v ? '✓' : '-' },
      },
      {
        name: 'agreedByManagerAt',
        label: t('onboardingGoal.agreedByManager'),
        options: { filter: false, sort: false, customBodyRender: (v) => v ? '✓' : '-' },
      },
    ];
  }, [t, data]);

  const objectSchema = useMemo(() => {
    const periodLabel = (s) => t(`onboardingGoal.period.${s}`, { defaultValue: s });
    return [
      {
        name: 'onboardingId', label: t('onboardingGoal.person'), type: 'asyncSelect', required: true,
        onSearch: async (q) => {
          const params = q ? { 'personName[like]': q, pageSize: 20 } : { pageSize: 20 };
          const res = await api.get('/v1/onboardings', { params });
          const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
          return items.map((o) => ({ id: o.id, name: o.personFullName }));
        },
        onGetById: async (id) => {
          const res = await api.get(`/v1/onboardings/${id}`);
          const o = res.data?.data || {};
          return { id, name: o.personFullName || '' };
        },
      },
      {
        name: 'period', label: t('onboardingGoal.periodLabel'), type: 'select', required: true, grid: 6,
        options: GOAL_PERIODS.map((p) => ({ id: p, name: periodLabel(p) })),
      },
      { name: 'title', label: t('onboardingGoal.title'), type: 'text', required: true },
      { name: 'description', label: t('onboardingGoal.description'), type: 'textarea' },
      { name: 'measurementCriteria', label: t('onboardingGoal.measurementCriteria'), type: 'textarea' },
    ];
  }, [t]);

  return { fetching, data, pagination, columns, objectSchema, toFormValues, refresh, create, update, start, cancel, agreeManager, score };
};

export default useObGoalsApi;
